import type { StaffCatalogPriceClass } from "@lafayette/schemas";
import { useMemo, useState } from "react";
import { Button } from "../../ui/button";
import { cn } from "../../ui/cn";
import { CheckboxField, SelectField, TextAreaField, TextField } from "../../ui/fields";
import { Notice } from "../../ui/notice";
import { SectionCard } from "../../ui/section-card";
import { SectionForm } from "../../ui/section-form";
import { StatusBadge, type StatusTone } from "../../ui/status-badge";
import { describeOutcome } from "../commands";
import { ReasonDialog } from "../components/ReasonDialog";
import { fieldGrid, hintText, plainList } from "../components/styles";
import {
  type PricingKey,
  pricingAmountFields,
  pricingValuesFromItem,
  pricingValuesToInput,
} from "../form-mappers";
import {
  formatDateTime,
  priceClassDescriptions,
  priceClassLabels,
  priceSourceOptions,
  priceUnitOptions,
  pricingModelOptions,
} from "../labels";
import { paymentBlockers } from "../publishability";
import { errorFieldsOf, type ItemSectionContext, useSectionState } from "./section-support";

const priceClassTones: Readonly<Record<StaffCatalogPriceClass, StatusTone>> = {
  PAYMENT_GRADE: "success",
  FIXED_NOT_PAYABLE: "warning",
  INDICATIVE: "neutral",
  QUOTE: "neutral",
  UNKNOWN: "neutral",
};

const ID_PREFIX = "pricing";

const pricingLabels: Readonly<Record<PricingKey, string>> = {
  model: "Pricing model",
  currency: "Currency",
  amount: "Price",
  minAmount: "Lowest price",
  maxAmount: "Highest price",
  unit: "Price unit",
  terms: "Price terms",
  source: "Where the price comes from",
  validFrom: "Valid from (UTC)",
  validUntil: "Valid until (UTC)",
  noExpiry: "The price does not expire",
};

const pricingOrder: readonly PricingKey[] = [
  "model",
  "currency",
  "amount",
  "minAmount",
  "maxAmount",
  "unit",
  "source",
  "validFrom",
  "validUntil",
  "terms",
];

/** The price, its class, and the authorization that makes a fixed price payment-grade. */
export function PricingSection({ ctx }: Readonly<{ ctx: ItemSectionContext }>) {
  const { detail, readOnly, command, capabilities } = ctx;
  const { item } = detail;
  const server = useMemo(() => pricingValuesFromItem(item), [item]);
  const section = useSectionState("pricing", server);
  const [authorizing, setAuthorizing] = useState(false);

  const amountFields = pricingAmountFields(section.values.model);
  const pricedModel = amountFields.length > 0;
  const set = (key: PricingKey, value: string) =>
    section.setValues((current) => ({ ...current, [key]: value }));

  async function submit() {
    const mapped = pricingValuesToInput(section.values);
    if (!mapped.ok) {
      section.reject(mapped.errors);
      return;
    }
    section.clearErrors();
    const outcome = await command.run("updatePricing", {
      expectedVersion: item.version,
      pricing: mapped.value,
    });
    section.setMessage(
      describeOutcome(
        outcome,
        item.priceAuthoritative
          ? "Price saved. The payment-grade mark was removed. Authorize the price again."
          : "Price saved.",
      ),
    );
    if (outcome.ok) section.reset(pricingValuesFromItem(outcome.detail.item));
  }

  async function authorize(reason: string): Promise<string | null> {
    const outcome = await command.run("authorizePrice", {
      expectedVersion: item.version,
      reason,
    });
    if (outcome.ok) {
      // Authorization sets only the mark. The server's price class stays "not payable" while another
      // rule fails (the item is not published, the unit, the end date, the currency).
      section.setMessage({
        tone: "success",
        text:
          outcome.detail.priceClass === "PAYMENT_GRADE"
            ? "The price is now payment-grade."
            : "The price is authorized. Customers cannot pay it yet: see the reasons under the price class.",
      });
      return null;
    }
    // The API answers 422 without a rule code. The rules of a payment-grade price are few, so say them.
    if (outcome.failure.kind === "invalid") {
      return "The server did not authorize this price. A payment-grade price needs an end date or “The price does not expire”, a positive amount, and the human concierge fulfilment mode.";
    }
    return describeOutcome(outcome, "").text;
  }

  const lacksValidity =
    item.priceValidUntil === null && !item.priceNoExpiry && item.pricingModel === "FIXED";

  const canAuthorize =
    capabilities.authorizePrice &&
    item.status !== "ARCHIVED" &&
    item.pricingModel === "FIXED" &&
    !item.priceAuthoritative &&
    !section.dirty;

  const errorFields = errorFieldsOf(
    section.errors,
    pricingOrder.map((key) => ({ key, id: `${ID_PREFIX}-${key}`, label: pricingLabels[key] })),
  );
  const disabled = readOnly || command.pending;
  const field = (key: PricingKey) => `${ID_PREFIX}-${key}`;

  return (
    <SectionCard
      id="pricing"
      title="Price"
      description="What customers see and whether they can pay it."
    >
      <div className="grid gap-1">
        <p className="m-0 flex flex-wrap items-center gap-2">
          <strong>Price class:</strong>
          <StatusBadge tone={priceClassTones[detail.priceClass]}>
            {priceClassLabels[detail.priceClass]}
          </StatusBadge>
        </p>
        <p className={hintText}>{priceClassDescriptions[detail.priceClass]}</p>
        {paymentBlockers(detail).length > 0 ? (
          <ul className={cn(plainList, hintText)}>
            {paymentBlockers(detail).map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        ) : null}
        {item.priceAuthoritative && item.priceAuthorizedAt !== null ? (
          <p className={hintText}>Authorized {formatDateTime(item.priceAuthorizedAt)}.</p>
        ) : null}
      </div>

      {item.priceAuthoritative && section.dirty ? (
        <Notice tone="warning" role="status">
          Saving a price change removes the payment-grade mark. The price must be authorized again
          before customers can pay it.
        </Notice>
      ) : null}

      <SectionForm
        label="Price"
        dirty={section.dirty}
        pending={command.pending}
        readOnly={readOnly}
        message={section.message}
        errorFields={errorFields}
        errorRevision={section.revision}
        onSubmit={submit}
        onDiscard={section.discard}
      >
        <div className={fieldGrid}>
          <SelectField
            id={field("model")}
            label={pricingLabels.model}
            value={section.values.model}
            onChange={(value) => set("model", value)}
            options={pricingModelOptions}
            error={section.errors.model}
            disabled={disabled}
          />
          {pricedModel ? (
            <>
              <TextField
                id={field("currency")}
                label={pricingLabels.currency}
                value={section.values.currency}
                onChange={(value) => set("currency", value)}
                error={section.errors.currency}
                disabled={disabled}
                maxLength={3}
                hint="Three letters, for example EUR."
              />
              {amountFields.map((key) => (
                <TextField
                  key={key}
                  id={field(key)}
                  label={pricingLabels[key]}
                  value={section.values[key]}
                  onChange={(value) => set(key, value)}
                  error={section.errors[key]}
                  disabled={disabled}
                  inputMode="decimal"
                  hint="In the currency's main unit, for example 120.50."
                />
              ))}
              <SelectField
                id={field("unit")}
                label={pricingLabels.unit}
                value={section.values.unit}
                onChange={(value) => set("unit", value)}
                options={priceUnitOptions}
                placeholder="Not set"
                error={section.errors.unit}
                disabled={disabled}
              />
              <SelectField
                id={field("source")}
                label={pricingLabels.source}
                value={section.values.source}
                onChange={(value) => set("source", value)}
                options={priceSourceOptions}
                placeholder="Not set"
                error={section.errors.source}
                disabled={disabled}
              />
              <TextField
                id={field("validFrom")}
                label={pricingLabels.validFrom}
                type="datetime-local"
                value={section.values.validFrom}
                onChange={(value) => set("validFrom", value)}
                error={section.errors.validFrom}
                disabled={disabled}
              />
              <TextField
                id={field("validUntil")}
                label={pricingLabels.validUntil}
                type="datetime-local"
                value={section.values.validUntil}
                onChange={(value) => set("validUntil", value)}
                error={section.errors.validUntil}
                disabled={disabled}
              />
              <CheckboxField
                id={field("noExpiry")}
                label={pricingLabels.noExpiry}
                checked={section.values.noExpiry === "true"}
                onChange={(checked) => set("noExpiry", checked ? "true" : "false")}
                disabled={disabled}
              />
            </>
          ) : null}
          <div className="col-span-full">
            <TextAreaField
              id={field("terms")}
              label={pricingLabels.terms}
              value={section.values.terms}
              onChange={(value) => set("terms", value)}
              error={section.errors.terms}
              disabled={disabled}
              rows={2}
              maxLength={500}
            />
          </div>
        </div>
      </SectionForm>

      {canAuthorize ? (
        <div className="grid justify-items-start gap-2">
          <p className={hintText}>
            This fixed price is not authorized for payment. Authorizing it lets a customer pay it.
            {lacksValidity
              ? " Before you can authorize it, give it an end date or tick “The price does not expire”."
              : ""}
          </p>
          <Button type="button" onClick={() => setAuthorizing(true)} disabled={command.pending}>
            Authorize price for payment
          </Button>
        </div>
      ) : null}
      {authorizing ? (
        <ReasonDialog
          title="Authorize this price for payment"
          description="Customers will be able to pay exactly this price. Say why it is correct."
          confirmLabel="Authorize price"
          onConfirm={authorize}
          onClose={() => setAuthorizing(false)}
        />
      ) : null}
    </SectionCard>
  );
}
